/**
 * Additional backup platforms - the registry vendors, as tiles.
 *
 * These are NOT the sources above. Those five are proven: they collect on a
 * schedule and write to the board. Everything here was written from vendor
 * documentation and has never been run against a live account, so it can fetch
 * and it can show you what it found, and it cannot write a single row until
 * somebody looks at that output and says so.
 *
 * WHY THE TILE LEADS WITH CONFIDENCE. The natural thing is to render these
 * exactly like the working integrations, which would make an unproven mapper
 * indistinguishable from a verified one at a glance - and the entire risk here
 * is somebody trusting a number that was never checked. So the confidence
 * badge sits under every tile, and "verified" is a state you can only reach by
 * running a dry run and reading it.
 *
 * The dry run deliberately surfaces two things that look like noise and are
 * not: unrecognised enum values (the mapping table is incomplete) and rows the
 * vendor calls healthy whose last success is ancient (the mapping table is
 * WRONG). The second one is the MSP360 near-miss, made visible before anything
 * is trusted rather than after.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { errText } from './btApi';
import { VendorTile, type TileBadge } from './components/VendorTile';
import { Modal } from './components/Modal';

function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

type Capability = 'collect' | 'tickets' | 'history' | 'knowledge';
type Confidence = 'verified' | 'documented' | 'inferred';

interface VendorField {
  key: string; label: string; secret: boolean; placeholder: string | null;
}

interface Vendor {
  id: string;
  label: string;
  capabilities: Capability[];
  confidence: Confidence;
  docs: string;
  notes: string | null;
  fields: VendorField[];
  defaultBaseUrl: string;
  configured: boolean;
  enabled: boolean;
  verified: boolean;
  verifiedBy: string | null;
  baseUrl: string | null;
  /** Initials on a colour, for vendors who publish no usable logo. */
  mark?: { initials: string; colour: string; ink: string };
}

interface DryRunReport {
  vendorId: string;
  label: string;
  confidence: string;
  ok: boolean;
  error: string | null;
  persisted: false;
  rowCount: number;
  sample: { name: string; client: string; status: string; reason: string; lastSuccessIso: string | null }[];
  statusCounts: Record<string, number>;
  unrecognised: string[];
  vendorSaysOkButStale: { name: string; hours: number }[];
  durationMs: number;
}

const listVendors = call<{ orgId: string }, { vendors: Vendor[] }>('listVendors');
const saveVendor = call<{ orgId: string; vendorId: string; enabled: boolean; values: Record<string, string> },
  { ok: boolean; verificationCleared: boolean }>('setVendorCredentials');
const clearVendor = call<{ orgId: string; vendorId: string }, { ok: boolean }>('clearVendorCredentials');
const dryRun = call<{ orgId: string; vendorId: string }, DryRunReport>('runVendorDryRun');
const setVerified = call<{ orgId: string; vendorId: string; verified?: boolean },
  { ok: boolean; verified: boolean; rowCount?: number }>('setVendorVerified');
const preview = call<{ orgId: string; vendorId: string; send?: boolean },
  { vendor: string; action: string; dryRun: boolean; payload: unknown; sent?: boolean; error?: string }>('previewOutbound');
const importItglue = call<{ orgId: string; store?: boolean; limit?: number },
  { preview: boolean; count: number; sample?: { title: string; organization: string }[] }>('importItglueKnowledge');

const CAP_LABEL: Record<Capability, string> = {
  collect: 'collects backups',
  tickets: 'creates tickets',
  history: 'receives history',
  knowledge: 'provides articles',
};

const CONFIDENCE_NOTE: Record<Confidence, string> = {
  verified: 'Proven against a live account.',
  documented: 'Endpoints and auth come from the vendor\'s own reference. Response shapes are unproven.',
  inferred: 'Assembled from SDKs and community docs. Expect the mapping to need work.',
};

function badgesFor(v: Vendor): TileBadge[] {
  const out: TileBadge[] = [];
  // The loudest thing on the tile, on purpose.
  out.push(v.verified
    ? { text: 'verified', tone: 'ok' }
    : { text: v.confidence, tone: v.confidence === 'documented' ? 'warn' : 'bad' });
  if (v.configured) out.push({ text: 'configured', tone: 'ok' });
  return out;
}

export function VendorRegistryPanel({ orgId, kind }: {
  orgId: string;
  /** Which half of the registry this panel shows. */
  kind: 'collect' | 'outbound';
}) {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { setVendors((await listVendors({ orgId })).vendors); }
    catch (e) { setError(errText(e)); }
    finally { setLoading(false); }
  };
  /**
   * LOAD ON MOUNT, NOT ON EXPAND.
   *
   * The header says "show 13", and that number came from a list this effect
   * only fetched once the section was already open - so it read "show 0" until
   * you expanded it, corrected itself, and then stayed correct. The whole point
   * of the count is to tell you whether opening the section is worth it, and a
   * count that is only right AFTER you open it answers a question nobody still
   * has.
   */
  useEffect(() => { void load(); }, [orgId]);

  const collectors = vendors.filter((v) => v.capabilities.includes('collect'));
  const outbound = vendors.filter((v) => !v.capabilities.includes('collect'));
  const shown = kind === 'collect' ? collectors : outbound;

  const title = kind === 'collect'
    ? 'More backup platforms'
    : 'Ticketing, documentation and compliance';

  return (
    <section className="panel" style={{ marginTop: '1rem' }}>
      <div className="integ-head">
        <h3 className="panel-title" style={{ margin: 0 }}>{title}</h3>
        <button className="btn-link" onClick={() => setOpen(!open)}>
          {/* No number until there is a real one. "show 0" on a section that
              holds thirteen vendors is worse than "show" - it is a confident
              wrong answer, and a reader cannot tell it from a section that
              genuinely has nothing in it. */}
          {open ? 'hide' : loading && vendors.length === 0 ? 'show' : `show ${shown.length}`}
        </button>
      </div>

      {open && (
        <>
          {error && <div className="banner banner-error">{error}</div>}
          {loading && vendors.length === 0 ? <div className="empty">Loading…</div> : (
            <>
              <div className="vgrid">
                {shown.map((v) => (
                  <VendorTile
                    key={v.id} id={v.id} label={v.label} mark={v.mark}
                    badges={badgesFor(v)}
                    selected={openId === v.id}
                    onClick={() => setOpenId(openId === v.id ? null : v.id)}
                  />
                ))}
              </div>
              {shown.filter((v) => v.id === openId).map((v) => (
                <VendorDetail key={v.id} orgId={orgId} vendor={v}
                              onChanged={load} onClose={() => setOpenId(null)} />
              ))}
            </>
          )}
        </>
      )}
    </section>
  );
}

function VendorDetail({ orgId, vendor, onChanged, onClose }: {
  orgId: string; vendor: Vendor; onChanged: () => void; onClose: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState(vendor.enabled);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [report, setReport] = useState<DryRunReport | null>(null);

  async function run(key: string, fn: () => Promise<unknown>, refresh = true) {
    setBusy(key); setErr(null); setMsg(null);
    try { await fn(); if (refresh) onChanged(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(null); }
  }

  const dirty = Object.values(values).some((v) => v !== '');

  const isCollector = vendor.capabilities.includes('collect');

  return (
    <Modal
      title={vendor.label}
      subtitle={vendor.capabilities.map((c) => CAP_LABEL[c]).join(' \u00b7 ')}
      onClose={onClose}
      dirty={dirty}
    >

      {!vendor.verified && (
        <div className="dim" style={{ fontSize: 12, marginBottom: 8 }}>
          {CONFIDENCE_NOTE[vendor.confidence]}
          {isCollector && ' Nothing from here reaches the dashboard yet.'}
        </div>
      )}

      {err && <div className="banner banner-error">{err}</div>}
      {msg && <pre className="banner probe-out">{msg}</pre>}

      {vendor.notes && (
        <p className="panel-note" style={{ marginTop: 0 }}>
          {vendor.notes} <a href={vendor.docs} target="_blank" rel="noreferrer">Vendor API docs</a>
        </p>
      )}

      <div className="row-form">
        {vendor.fields.map((f) => (
          <label className="field" key={f.key}>
            <span className="field-label">{f.label}</span>
            <input
              className="field-input"
              type={f.secret ? 'password' : 'text'}
              autoComplete={f.secret ? 'new-password' : 'off'}
              placeholder={f.placeholder ?? (f.secret ? 'not set' : '')}
              value={values[f.key] ?? ''}
              onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
            />
          </label>
        ))}
      </div>

      <label className="check-row">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span>Enabled</span>
      </label>

      <div className="row-form" style={{ marginTop: '.6rem' }}>
        <button
          className="btn btn-primary" disabled={busy !== null}
          onClick={() => run('save', async () => {
            const r = await saveVendor({ orgId, vendorId: vendor.id, enabled, values });
            setValues({});
            setMsg(r.verificationCleared
              // Silently keeping verification would let a re-pointed base URL
              // inherit trust it never earned.
              ? 'Saved. Verification was cleared because the credentials changed — run a dry run again.'
              : 'Saved.');
          })}
        >
          {busy === 'save' ? 'Saving…' : 'Save'}
        </button>

        {isCollector && (
          <button
            className="btn" disabled={busy !== null || !vendor.configured}
            title="Fetch and map. Writes nothing."
            onClick={() => run('dry', async () => {
              const r = await dryRun({ orgId, vendorId: vendor.id });
              setReport(r);
              if (!r.ok) setErr(r.error);
            }, false)}
          >
            {busy === 'dry' ? 'Running…' : 'Dry run'}
          </button>
        )}

        {!isCollector && (
          <button
            className="btn" disabled={busy !== null || !vendor.configured}
            title="Build the exact payload without sending it."
            onClick={() => run('prev', async () => {
              const r = await preview({ orgId, vendorId: vendor.id });
              setMsg(
                `${r.action} — NOT SENT. This is exactly what would go to ${vendor.label}:\n\n` +
                JSON.stringify(r.payload, null, 2),
              );
            }, false)}
          >
            {busy === 'prev' ? 'Building…' : 'Preview payload'}
          </button>
        )}

        {vendor.capabilities.includes('knowledge') && (
          <button
            className="btn" disabled={busy !== null || !vendor.configured}
            title="Read documents so they can be matched against machine errors."
            onClick={() => run('kb', async () => {
              const r = await importItglue({ orgId });
              setMsg(
                `Found ${r.count} document(s). Nothing imported yet.\n\n` +
                (r.sample ?? []).map((d) => `• ${d.title}${d.organization ? ` — ${d.organization}` : ''}`).join('\n'),
              );
            }, false)}
          >
            {busy === 'kb' ? 'Reading…' : 'Preview documents'}
          </button>
        )}

        {vendor.configured && (
          <button
            className="btn" disabled={busy !== null}
            onClick={() => run('clear', async () => {
              await clearVendor({ orgId, vendorId: vendor.id });
              setReport(null);
              setMsg('Credentials removed.');
            })}
          >
            Clear
          </button>
        )}
      </div>

      {report && <DryRunResult report={report} vendor={vendor} orgId={orgId} onChanged={onChanged} />}
    </Modal>
  );
}

/**
 * The dry run result, and the only route to verification.
 *
 * Ordered by how much it should worry you rather than by how the data is
 * structured: unrecognised enums first (the table is incomplete), then healthy
 * rows with an ancient last success (the table is wrong in the dangerous
 * direction), then the sample. Counts alone would let both hide.
 */
function DryRunResult({ report, vendor, orgId, onChanged }: {
  report: DryRunReport; vendor: Vendor; orgId: string; onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!report.ok) {
    return <div className="banner banner-error" style={{ marginTop: 10 }}>{report.error}</div>;
  }

  const blocked = report.unrecognised.length > 0 || report.rowCount === 0;

  return (
    <div style={{ marginTop: 12 }}>
      <div className="flbl">
        Dry run — {report.rowCount} row(s) in {report.durationMs}ms. Nothing was written.
      </div>

      {report.unrecognised.length > 0 && (
        <div className="banner banner-error" style={{ marginTop: 6 }}>
          <strong>The mapping is incomplete.</strong> {report.unrecognised.length} status value(s)
          were not recognised, so those machines are showing as unknown:
          <ul style={{ margin: '.4rem 0 0 1rem' }}>
            {report.unrecognised.slice(0, 5).map((u) => <li key={u}>{u}</li>)}
          </ul>
        </div>
      )}

      {report.vendorSaysOkButStale.length > 0 && (
        <div className="banner banner-error" style={{ marginTop: 6 }}>
          <strong>Check these before trusting anything.</strong> The vendor calls them healthy, but
          their last success is old — which is what a wrong status mapping looks like:
          <ul style={{ margin: '.4rem 0 0 1rem' }}>
            {report.vendorSaysOkButStale.slice(0, 5).map((s) => (
              <li key={s.name}>{s.name} — {Math.round(s.hours / 24)} day(s) ago</li>
            ))}
          </ul>
        </div>
      )}

      <div className="row-form" style={{ marginTop: 6, gap: 8 }}>
        {Object.entries(report.statusCounts).map(([k, n]) => (
          <span key={k} className="tag">{k}: {n}</span>
        ))}
      </div>

      <table className="tbl" style={{ marginTop: 8 }}>
        <thead><tr><th>Name</th><th>Client</th><th>Status</th><th>Last success</th><th>Why</th></tr></thead>
        <tbody>
          {report.sample.map((r, i) => (
            <tr key={i}>
              <td>{r.name}</td>
              <td className="dim">{r.client || '—'}</td>
              <td>{r.status}</td>
              <td className="dim">{r.lastSuccessIso ? r.lastSuccessIso.slice(0, 10) : '—'}</td>
              <td className="dim" style={{ fontSize: 11 }}>{r.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {err && <div className="banner banner-error" style={{ marginTop: 8 }}>{err}</div>}

      <p className="panel-note">
        Compare these rows against {vendor.label}&rsquo;s own console. If they match, verify &mdash;
        that is the only thing that lets this vendor write to your dashboard.
      </p>

      <button
        className="btn btn-primary"
        disabled={busy || blocked || vendor.verified}
        title={blocked ? 'Fix the mapping first' : 'Allow this vendor to write to the dashboard'}
        onClick={async () => {
          setBusy(true); setErr(null);
          try { await setVerified({ orgId, vendorId: vendor.id }); onChanged(); }
          catch (e) { setErr(errText(e)); }
          finally { setBusy(false); }
        }}
      >
        {vendor.verified ? 'Verified' : busy ? 'Verifying…' : 'These rows are correct — verify'}
      </button>
    </div>
  );
}
